import { getDefaultConfig } from "@rainbow-me/rainbowkit";
import { getChainId, getWalletClient, switchChain } from "wagmi/actions";

import { getSupportedEvmChainsWithRpc, getTransports } from "@/config/chains/helpers";
import { publicEnv } from "@/config/publicEnv";
import { walletList } from "@/config/wallets";

const chains = getSupportedEvmChainsWithRpc().map((chain) => chain.chain);

export const config = getDefaultConfig({
  appName: "Agora Web App",
  chains: [chains[0], ...chains.slice(1)],
  projectId: publicEnv.NEXT_PUBLIC_REOWN_PROJECT_ID || "default-project-id",
  ssr: true,
  transports: getTransports(),
  wallets: walletList,
});

/**
 * Resolves a WalletClient for a given chainId using your configured wagmi client.
 */
export async function getWalletClientForChain(chainId: number) {
  return await getWalletClient(config, { chainId });
}

/**
 * Ensures the wallet is connected to the specified chain before returning the wallet client.
 * If the wallet is on a different chain, it will prompt the user to switch chains first.
 * @param chainId - The chain ID to ensure the wallet is connected to
 * @returns Promise that resolves with the wallet client once on the correct chain
 */
export async function getWalletClientWithChainSwitch(chainId: number) {
  try {
    // Check current chain ID
    const currentChainId = getChainId(config);

    // If we're already on the correct chain, just get the wallet client
    if (currentChainId === chainId) {
      const walletClient = await getWalletClient(config, { chainId });

      if (!walletClient) {
        throw new Error("Wallet client not found for the specified chain");
      }

      if (!walletClient.account) {
        throw new Error("Wallet client is not configured with an account");
      }

      return walletClient;
    }

    // If we're on a different chain, switch first
    console.log(`Switching from chain ${currentChainId} to chain ${chainId}`);
    await switchChain(config, { chainId });

    // Wait a bit for the chain switch to complete
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Verify the chain switch was successful
    const newChainId = getChainId(config);
    if (newChainId !== chainId) {
      throw new Error(
        `Chain switch failed. Expected chain ${chainId}, but wallet is still on chain ${newChainId}. Please manually switch to the correct network in your wallet.`,
      );
    }

    // Now get the wallet client for the switched chain
    const walletClient = await getWalletClient(config, { chainId });

    if (!walletClient) {
      throw new Error("Wallet client not found for the specified chain");
    }

    if (!walletClient.account) {
      throw new Error("Wallet client is not configured with an account");
    }

    return walletClient;
  } catch (error) {
    console.error("Failed to switch chain or get wallet client:", error);

    // Provide more helpful error messages
    if (error instanceof Error) {
      if (error.message.includes("User rejected")) {
        throw new Error(
          "Chain switch was rejected. Please manually switch to the correct network in your wallet and try again.",
        );
      }
      if (error.message.includes("Chain switch failed")) {
        throw error; // Re-throw our custom error
      }
    }

    throw new Error(
      `Failed to switch to chain ${chainId}. Please ensure your wallet supports this network and try again.`,
    );
  }
}
